/**
 * Widget SSO authentication utilities.
 *
 * Flow: Widget opens system browser → user logs in (SSO or password) →
 * JWT redirected back to widget via recare-widget:// custom protocol.
 *
 * sessionStorage is used to persist the widget flag across the SSO redirect
 * chain (Frontend → FusionAuth → IdP → Backend → Frontend) since it survives
 * cross-origin navigations within the same tab.
 */

import { getQueryBooleanVariable } from "core/model/utils/urls";
import jwtDecode from "jwt-decode";

const WIDGET_SESSION_KEY = "widget_auth_pending";
const WIDGET_PROTOCOL = "recare-widget";
const ROLE_SCRIBE = 35;
const PRODUCT_FEATURE_SCRIBE = 10;

/**
 * Detect ?widget query param and persist in sessionStorage.
 * Called once on login page mount. The param is removed from the URL
 * to prevent re-triggering on refresh.
 */
export function initWidgetAuthFlow(): void {
  if (!getQueryBooleanVariable(window.location.search, "widget")) return;

  sessionStorage.setItem(WIDGET_SESSION_KEY, "true");

  const params = new URLSearchParams(window.location.search);
  params.delete("widget");
  const query = params.toString();
  const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ""}`;
  window.history.replaceState({}, "", cleanUrl);
}

/** Check if the current session is a widget authentication flow */
export function isWidgetAuthFlow(): boolean {
  return sessionStorage.getItem(WIDGET_SESSION_KEY) === "true";
}

export type JwtPayload = {
  exp?: number;
  r?: {
    cs?: Record<string, { r?: number[]; f?: number[] }>;
  };
  iat?: number;
  sub?: string;
};

export const WIDGET_SCRIBE_FEATURE_ERROR = "widget_scribe_feature_not_enabled";
export const WIDGET_SCRIBE_ROLE_ERROR = "widget_scribe_role_not_assigned";

export function validateScribeAccess(jwtPayload: JwtPayload): void {
  const careSeekerGrants = Object.values(jwtPayload?.r?.cs ?? {})[0];
  const hasFeature =
    careSeekerGrants?.f?.includes(PRODUCT_FEATURE_SCRIBE) ?? false;
  const hasRole = careSeekerGrants?.r?.includes(ROLE_SCRIBE) ?? false;

  // Propagated errors are catched by useHandleLogin hook and shown an a general form error.
  if (!hasFeature) {
    throw new Error(WIDGET_SCRIBE_FEATURE_ERROR);
  }
  if (!hasRole) {
    throw new Error(WIDGET_SCRIBE_ROLE_ERROR);
  }
}

/**
 * Redirect JWT to widget via custom protocol handler.
 * Uses a hidden iframe to trigger the protocol without navigating
 * the page, then shows a "you can close this tab" message.
 */
export function redirectToWidget(
  token: string,
  title: string,
  message: string,
): void {
  /**
   * Check if user has "Scribe" access which is required to access the desktop application.
   */
  const decodedToken = jwtDecode<JwtPayload>(token);
  validateScribeAccess(decodedToken);

  window.forceReload = true; // Custom flag to bypass unload warning if set
  sessionStorage.removeItem(WIDGET_SESSION_KEY);
  window.history.replaceState({}, "", "/auth");

  // Trigger custom protocol via anchor click — works on both HTTP and HTTPS.
  // Hidden iframes are silently blocked by Chrome/Safari on HTTPS pages.
  const a = document.createElement("a");
  a.href = `${WIDGET_PROTOCOL}://callback?token=${encodeURIComponent(token)}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Show a clean message after the protocol redirect fires.
  // Consistent for both SSO and password login — user closes the tab manually.
  setTimeout(() => {
    document.title = title;
    document.body.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666">' +
      `<p>${message}</p></div>`;
  }, 500);
}
